import type React from "react"
import { useEffect, useRef, useState, useMemo, memo, useCallback } from "react"

interface EmbedFrameProps {
  formURL: string
  autoResize?: boolean
  autoFocus?: boolean
  heightOffset?: number
  initialHeight?: number
  onSubmit?: () => void
  formID?: string
  style?: React.CSSProperties
  onLoad?: () => void
  className?: string
  [key: string]: any // For any additional props passed via ...rest
}

const EmbedFrame: React.FC<EmbedFrameProps> = ({
  formURL,
  autoResize = false,
  autoFocus = false,
  heightOffset = 15,
  initialHeight = 540,
  onSubmit,
  formID,
  style = {},
  onLoad,
  className,
  ...rest
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  const [minHeight, setMinHeight] = useState<number>(initialHeight)
  const submissionDetectedRef = useRef(false)

  // Reset submission detection when form URL changes
  useEffect(() => {
    submissionDetectedRef.current = false
  }, [formURL])

  const handleMessages = useCallback(
    (event: MessageEvent) => {
      // Debug all incoming messages
      console.log("EmbedFrame: Received message:", event.data)

      // Check if the message is from our iframe
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return
      }

      const content = event.data

      // Check for submission in various formats
      const isSubmissionCompleted =
        (typeof content === "object" && content?.action === "submission-completed") ||
        (typeof content === "object" && content?.formData && content?.formID) ||
        (typeof content === "string" && content.includes("submission-completed")) ||
        (typeof content === "string" && content.includes("formSubmitted")) ||
        (typeof content === "string" && content.includes("JotFormSubmitted"))

      if (isSubmissionCompleted && onSubmit && !submissionDetectedRef.current) {
        console.log("EmbedFrame: Form submission detected")
        submissionDetectedRef.current = true
        onSubmit()
        return
      }

      // Handle iframe height adjustments
      if (typeof content === "string") {
        const [method, value] = content.split(":")

        switch (true) {
          case method === "scrollIntoView" && autoFocus:
            if (iframeRef.current && typeof iframeRef.current.scrollIntoView === "function") {
              iframeRef.current.scrollIntoView()
            }
            break
          case method === "setHeight" && autoResize:
            setHeight(Number.parseInt(value, 10) + heightOffset)
            break
          case method === "setMinHeight" && autoResize:
            setMinHeight(Number.parseInt(value, 10) + heightOffset)
            break
          case method === "reloadPage":
            try {
              if (iframeRef.current && iframeRef.current.contentWindow) {
                iframeRef.current.contentWindow.location.reload()
              }
            } catch (e) {
              console.error("failed to reload", e)
            }
            break
          default:
            break
        }
      }
    },
    [autoFocus, autoResize, heightOffset, onSubmit]
  )

  // Set up event listener for iframe messages
  useEffect(() => {
    window.addEventListener("message", handleMessages)

    return () => {
      window.removeEventListener("message", handleMessages)
    }
  }, [handleMessages])

  // Update iframe height when it changes
  useEffect(() => {
    if (iframeRef.current && height) {
      iframeRef.current.style.height = `${height}px`
      iframeRef.current.style.minHeight = `${height}px`
      if (onLoad) {
        onLoad()
      }
    }
  }, [height, onLoad])

  // Add a direct event listener to the iframe for form submission
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe || !onSubmit) return

    const checkForFormSubmission = () => {
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document
        if (!iframeDocument) return

        // Look for success message or thank you page
        const successElements = iframeDocument.querySelectorAll('.jf-thankyou, .jf-success-message, .jf-form-complete')

        if (successElements.length > 0 && !submissionDetectedRef.current) {
          console.log("EmbedFrame: Form success elements detected in iframe")
          submissionDetectedRef.current = true
          onSubmit()
        }
      } catch (e) {
        // CORS might prevent access to iframe content
        console.log("EmbedFrame: Could not access iframe content due to CORS")
      }
    }

    // Check periodically for form submission
    const interval = setInterval(checkForFormSubmission, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [onSubmit, formURL])

  // Add a direct event listener to detect form submission via URL change
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe || !onSubmit) return

    const handleIframeLoad = () => {
      try {
        const currentUrl = iframe.contentWindow?.location.href
        if (currentUrl && (currentUrl.includes("thankyou") || currentUrl.includes("success"))) {
          console.log("EmbedFrame: Form submission detected via URL change")
          if (!submissionDetectedRef.current) {
            submissionDetectedRef.current = true
            onSubmit()
          }
        }
      } catch (e) {
        // CORS might prevent access to iframe location
        console.log("EmbedFrame: Could not access iframe location due to CORS")
      }
    }

    iframe.addEventListener('load', handleIframeLoad)

    return () => {
      iframe.removeEventListener('load', handleIframeLoad)
    }
  }, [onSubmit, formURL])

  return useMemo(() => {
    return (
      <iframe
        id="iframe_id"
        ref={iframeRef}
        src={formURL}
        title="JotForm Form"
        className={className}
        style={{
          overflow: "hidden",
          border: 0,
          width: "100%",
          minHeight: `${minHeight}px`,
          ...style,
        }}
        allowFullScreen={true}
        allow="geolocation; microphone; camera"
        frameBorder="0"
        scrolling="no"
        {...rest}
      />
    )
  }, [formURL, rest, style, className, minHeight])
}

export default memo(EmbedFrame)